import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Clock,
  Search,
  FileStack,
  FileText,
  Loader2,
  ArrowRight,
} from "lucide-react";
import { useInProgressDocuments, type ScannedDocument } from "@/hooks/useDocuments";
import { formatDistanceToNow } from "date-fns";

interface InProgressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatFileSize(bytes: number | null) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusIndicator({ status }: { status: ScannedDocument["status"] }) {
  switch (status) {
    case "detected":
      return (
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <FileStack className="h-3.5 w-3.5" />
          <span>Queued for parsing</span>
        </div>
      );
    case "parsing":
      return (
        <div className="flex items-center gap-1.5 text-xs text-blue-600">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>AI OCR in progress...</span>
        </div>
      );
    case "routing":
      return (
        <div className="flex items-center gap-1.5 text-xs text-indigo-600">
          <ArrowRight className="h-3.5 w-3.5" />
          <span>Moving to Dropbox...</span>
        </div>
      );
    default:
      return null;
  }
}

export function InProgressDialog({ open, onOpenChange }: InProgressDialogProps) {
  const [search, setSearch] = useState("");
  const { data: documents, isLoading } = useInProgressDocuments();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearch("");
    }
    onOpenChange(open);
  };

  const query = search.toLowerCase();
  const filteredDocs = query && documents
    ? documents.filter((doc) =>
        doc.original_filename.toLowerCase().includes(query)
      )
    : documents || [];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl w-[90vw] h-[70vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-500" />
            In Progress
            <Badge variant="outline" className="ml-auto text-sm font-normal">
              {documents?.length || 0} documents
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col flex-1 min-h-0">
          {/* Search */}
          <div className="relative px-4 py-3 shrink-0">
            <Search className="absolute left-7 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by filename..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 focus-visible:ring-1 focus-visible:ring-border focus-visible:ring-offset-0"
            />
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {isLoading ? (
              <div className="space-y-3 p-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-14 w-full" />
                ))}
              </div>
            ) : !filteredDocs.length ? (
              <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                <FileText className="h-10 w-10 text-muted-foreground/50 mb-3" />
                <p className="text-sm font-medium">
                  {search ? "No matching documents" : "No documents currently processing"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {search ? "Try a different search term" : "Documents will appear here when being processed"}
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredDocs.map((doc) => (
                  <div
                    key={doc.id}
                    className="px-4 py-3 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <FileStack className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm font-medium truncate">{doc.original_filename}</span>
                      </div>
                      <StatusIndicator status={doc.status} />
                    </div>
                    <div className="flex items-center gap-3 mt-1 ml-6">
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(doc.detected_at), { addSuffix: true })}
                      </span>
                      {doc.file_size_bytes && (
                        <span className="text-xs text-muted-foreground">
                          {formatFileSize(doc.file_size_bytes)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
